import React, { useState, useEffect } from "react";
import "./inventory-theme.css";

export default function UpdateStockModal({ isOpen, onClose, item, onUpdate }) {
  const [mode, setMode] = useState("add");
  const [delta, setDelta] = useState(""); // stores user input (string or number)
  const [reason, setReason] = useState("");
  const [expiry, setExpiry] = useState("");

  useEffect(() => {
    if (item) {
      setExpiry(item.expiry_date || "");
      setDelta(""); 
      setReason("");
      setMode("add");
    }
  }, [item]);

  if (!isOpen || !item) return null;

  // Helper: parse numeric value out of possibly formatted strings like "10.00 pcs"
  const parseQuantity = (val) => {
    if (val === null || val === undefined || val === "") return 0;
    // If already number, return it
    if (typeof val === "number" && isFinite(val)) return val;
    // Convert to string and strip non-numeric except . and -
    const cleaned = String(val).replace(/[^0-9.\-]/g, "");
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };

  // Clean input handler: make sure delta becomes a number (or empty string)
  const handleDeltaChange = (value) => {
    // remove spaces
    let v = String(value).trim();

    if (item.unit === "pcs") {
      // keep only digits, drop decimals
      v = v.replace(/\D/g, ""); // remove non-digits
      // remove leading zeros but allow single 0
      v = v.replace(/^0+(?=\d)/, "");
      setDelta(v === "" ? "" : Number(v));
    } else {
      // allow decimal numbers: keep digits and dot and minus
      v = v.replace(/[^0-9.\-]/g, "");
      // avoid multiple dots
      const parts = v.split(".");
      if (parts.length > 2) {
        v = parts.shift() + "." + parts.join("");
      }
      // remove leading zeros like "00" -> "0" but keep "0.5"
      if (/^0\d+/.test(v) && !v.startsWith("0.")) v = String(Number(v));
      setDelta(v === "" ? "" : Number(v));
    }
  };

  // Numeric values used for calculation
  const currentQty = parseQuantity(item.quantity);

  const numericDelta = Number(delta || 0);

  // raw numeric total
  const rawTotal =
    mode === "add" ? currentQty + numericDelta : currentQty - numericDelta;

  // formatted new total (for display)
  let newTotalDisplay;
  if (item.unit === "pcs") {
    // pcs => show integer
    newTotalDisplay = String(Math.round(rawTotal));
  } else {
    // others => show 2 decimals
    // ensure rawTotal is a finite number
    const safe = isFinite(rawTotal) ? rawTotal : 0;
    newTotalDisplay = Number(safe).toFixed(2);
  }

  // formatted current display
  const currentDisplay =
    item.unit === "pcs"
      ? String(Math.round(currentQty))
      : Number(currentQty).toFixed(2);

  const submit = () => {
    // send numeric delta (exact) to backend
    onUpdate(item.id, mode, numericDelta, reason, expiry);
    onClose();
  };

  return (
    <div className="modal fade show d-block bg-dark bg-opacity-50">
      <div className="modal-dialog">
        <div className="modal-content soft-modal">
          <div className="soft-modal-header d-flex justify-content-between">
            <div>
              <h4 className="fw-bold">Update Stock</h4>
              <small className="text-muted">{item.name}</small>
            </div>
            <button className="btn btn-light rounded-circle" onClick={onClose}>
              ✕
            </button>
          </div>

          <div className="soft-modal-body">
            {/* Toggle */}
            <div className="d-flex gap-2 mb-4">
              <button
                className={`soft-toggle-btn ${mode === "add" ? "active" : ""}`}
                onClick={() => setMode("add")}
              >
                + Add Stock
              </button>

              <button
                className={`soft-toggle-btn ${
                  mode === "remove" ? "active" : ""
                }`}
                onClick={() => setMode("remove")}
              >
                – Record Waste/Use
              </button>
            </div>

            {/* Quantity */}
            <label className="form-label fw-semibold">Quantity</label>
            <div className="input-group mb-3">
              <input
                type="text"
                className="form-control soft-input"
                value={delta}
                onChange={(e) => handleDeltaChange(e.target.value)}
                placeholder={item.unit === "pcs" ? "10" : "0.00"}
              />
              <span className="input-group-text bg-light">{item.unit}</span>
            </div>

            {/* Reason */}
            <label className="form-label fw-semibold">Reason (optional)</label>
            <input
              className="form-control soft-input mb-3"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={mode === "add" ? "Delivery" : "Spoilage / Usage"}
            />

            {/* Totals */}
            <div className="p-3 bg-light rounded-4 d-flex justify-content-between mb-3">
              <div>
                <small className="text-muted">Current</small>
                <div className="fw-bold">
                  {currentDisplay} {item.unit}
                </div>
              </div>

              <div className="fw-bold fs-4 text-muted">→</div>

              <div className="text-end">
                <small className="text-muted">New Total</small>
                <div className="fw-bold text-primary">
                  {newTotalDisplay} {item.unit}
                </div>
              </div>
            </div>

            {/* Expiry (add mode) */}
            {mode === "add" && (
              <>
                <label className="form-label fw-semibold">Update Expiry</label>
                <input
                  type="date"
                  className="form-control soft-input"
                  value={expiry}
                  onChange={(e) => setExpiry(e.target.value)}
                />
              </>
            )}
          </div>

          <div className="soft-modal-footer">
            <button
              className={`btn w-100 fw-bold py-2 ${
                mode === "add" ? "btn-success" : "btn-danger"
              }`}
              onClick={submit}
            >
              {mode === "add" ? "Confirm Addition" : "Confirm Removal"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
